import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, ShieldCheck, Award, Sparkles } from "lucide-react";
import productsData from "@/data/products.json";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { BrandRibbon } from "@/components/BrandRibbon";
import { Testimonials } from "@/components/Testimonials";
import { useEnquiry } from "@/context/EnquiryContext";
import { useLang } from "@/context/LanguageContext";
import { BRAND } from "@/lib/site";

export default function Home() {
  const heroRef = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const featured = productsData.slice(0, 6);
  const heroLines = t("hero_lines");
  const stats = t("stats");
  const manifesto = t("manifesto");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-16">
        <div className="absolute inset-0 -z-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[120vh] w-[120vh] radial-glow" />
          <img src="/assets/logo.png" alt="" aria-hidden className="absolute right-[-12%] top-[8%] h-[70%] opacity-[0.05] blur-sm animate-spin-slow" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <motion.div style={{ y: textY, opacity: fade }}>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="label-eyebrow flex items-center gap-3">
              <span className="h-px w-10 bg-gold inline-block" /> {t("hero_eyebrow")}
            </motion.p>

            <h1 className="mt-6 font-serif font-light text-[clamp(3rem,8vw,6.5rem)] leading-[0.95] tracking-tight text-white">
              <MaskedLines lines={heroLines.map((l, i) => (i === 1 ? <span key={i} className="text-gold-gradient italic">{l}</span> : l))} />
            </h1>

            <Reveal delay={0.7} className="mt-8 max-w-lg text-lg text-white/65 font-light leading-relaxed">
              {t("hero_subtitle")}
            </Reveal>

            <Reveal delay={0.9} className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/products" data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors">
                {t("explore_collection")}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button onClick={() => openEnquiry(t("quick_enquiry"))} data-testid="hero-enquire-btn" className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-4 text-sm uppercase tracking-[0.2em] text-white hover:border-gold hover:text-gold transition-colors">
                {t("quick_enquiry")}
              </button>
            </Reveal>

            <Reveal delay={1.1} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-white/50">
              <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-gold" /> {t("badge_fssai")}</span>
              <span className="flex items-center gap-2"><Award size={15} className="text-gold" /> {t("badge_udyam")}</span>
              <span className="flex items-center gap-2"><Leaf size={15} className="text-gold" /> {t("badge_natural")}</span>
            </Reveal>
          </motion.div>

          <motion.div style={{ y: imgY }} className="relative flex justify-center">
            <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="relative">
              <div className="absolute inset-0 -z-10 scale-125 radial-glow" />
              <img src="/assets/makhana.png" alt="Mulywan Premium Makhana pack" className="w-[78%] mx-auto lg:w-full max-w-md animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]" />
              <span className="absolute -bottom-4 -left-2 md:left-2 rounded-full border border-gold/30 bg-navy-800/80 backdrop-blur px-4 py-2 text-xs text-gold flex items-center gap-2">
                <Sparkles size={13} /> {BRAND.name}
              </span>
            </motion.div>
          </motion.div>
        </div>

        <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-[10px] uppercase tracking-[0.3em] flex flex-col items-center gap-2">
          {t("scroll")}
          <span className="h-10 w-px bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      <BrandRibbon items={t("ribbon_home")} />

      {/* STATS */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-2xl border border-gold/15 bg-gold/10">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-navy-900 p-8 text-center">
              <p className="font-serif text-4xl md:text-5xl text-gold-gradient">{s.k}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/50">{s.v}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COLLECTION */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 md:px-10 py-12 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="label-eyebrow">{t("collection_eyebrow")}</p>
            <h2 className="mt-3 font-serif font-light text-4xl md:text-6xl text-white leading-none">
              {t("collection_t1")} <span className="text-gold-gradient italic">{t("collection_t2")}</span>
            </h2>
          </div>
          <Link to="/products" data-testid="home-view-all-btn" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors">
            {t("view_all")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative z-10 py-24 border-t border-gold/10">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal className="max-w-2xl mb-16">
            <p className="label-eyebrow">{t("manifesto_eyebrow")}</p>
            <h2 className="mt-3 font-serif font-light text-4xl md:text-6xl text-white leading-tight">{t("manifesto_title")}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10 md:gap-6">
            {manifesto.map((m, i) => (
              <Reveal key={i} delay={i * 0.12} className="relative pt-8 border-t border-gold/20">
                <span className="font-serif text-7xl text-gold/25 leading-none">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-3xl text-white">{m.title}</h3>
                <p className="mt-3 text-white/60 font-light leading-relaxed">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section className="relative z-10 py-8 pb-24">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal className="relative overflow-hidden rounded-3xl border border-gold/25 bg-navy-800 p-10 md:p-16 text-center">
            <div className="absolute inset-0 radial-glow opacity-70" />
            <div className="relative">
              <p className="label-eyebrow">{t("cta_eyebrow")}</p>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl text-white leading-tight">
                {t("cta_t1")} <span className="text-gold-gradient italic">{t("cta_t2")}</span>
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-white/60 font-light">{t("cta_body")}</p>
              <button onClick={() => openEnquiry(t("cta_eyebrow"))} data-testid="home-cta-enquire-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors">
                {t("start_enquiry")} <ArrowRight size={17} />
              </button>
              <p className="mt-6 text-xs text-white/40">{t("brand_by")} {BRAND.company} • {BRAND.addressShort}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
